"use client"

import React, { useState, useEffect, useRef } from 'react';
import { ref, set, onValue, get } from 'firebase/database';
import { getDatabase } from 'firebase/database';
import "./TransportGoods.css";
import { v4 as uuidv4 } from 'uuid';
import { FaInfoCircle } from 'react-icons/fa';
import { useCurrentUid } from "../hooks/useCurrentUid";

const gifUrl = "/Images/New notification.gif"

const TransportGoods = () => {
    const userId = useCurrentUid(); // This is your selected account (Main/Firm)

    const [formData, setFormData] = useState({
        destinationFrom: '',
        destinationTo: '',
        commodity: '',
        quantity: '',
        companyName: ''
    });

    const [requests, setRequests] = useState({});           // object: { transportId: data }
    const [replies, setReplies] = useState({});             // object: { transportId: { supplierId: reply } }
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    const unsubRequestsRef = useRef(null);
    const unsubRepliesRef = useRef(null);
    const prevUserIdRef = useRef(null);

    const commodityOptions = ['Cement', 'Steel'];

    const getIndianTimestamp = () => {
        const now = new Date();
        const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
        return ist.toISOString().replace("T", " ").substring(0, 19);
    };

    // Main effect — runs when account changes
    useEffect(() => {
        // Reset everything
        setLoading(true);
        setRequests({});
        setReplies({});
        setError("");
        setSuccess(false);

        // Cleanup old listeners
        if (unsubRequestsRef.current) unsubRequestsRef.current();
        if (unsubRepliesRef.current) unsubRepliesRef.current();

        if (!userId) {
            setLoading(false);
            return;
        }

        // Prevent duplicate listeners
        if (prevUserIdRef.current === userId) {
            setLoading(false);
            return;
        }
        prevUserIdRef.current = userId;

        const db = getDatabase();

        // Listen to user's transport requests
        const requestsRef = ref(db, `transport_goods/${userId}`);
        const unsubRequests = onValue(requestsRef, (snap) => {
            setRequests(snap.val() || {});
        });

        // Listen to supplier replies — THIS WAS BROKEN BEFORE
        const repliesRef = ref(db, `transport_goods_replies/${userId}`);
        const unsubReplies = onValue(repliesRef, (snap) => {
            setReplies(snap.val() || {});
            setLoading(false); // Stop loading only after both are loaded
        });

        unsubRequestsRef.current = unsubRequests;
        unsubRepliesRef.current = unsubReplies;

        return () => {
            unsubRequests();
            unsubReplies();
        };
    }, [userId]);

    const sendNotification = async (transportId, companyName) => {
        const notifRef = ref(getDatabase(), `supplier_notifications/transportGoods/${userId}/${transportId}`);
        await set(notifRef, {
            companyName,
            title: "TransportGoods",
            message: `A transport goods request has been sent by ${companyName}. Please review the query details.`,
            gifUrl,
            timestamp: getIndianTimestamp(),
            seen: false,
        }).catch(() => {});
    };

    const getSupplierClass = (supplierName) => {
        const map = {
            "Ultratech": "ultratech",
            "Shree": "shree",
            "JSW": "jsw",
            "JKSuper": "jksuper",
            "ACC": "acc",
            "Ambuja": "ambuja"
        };
        return map[supplierName] || "default";
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === "quantity" && value > 100000) {
            setError("You cannot enter more than 100000 bags.");
        } else {
            setError("");
        }
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userId) return alert("No account selected");
        if (formData.quantity > 100000) return;

        const transportId = uuidv4();
        const timestamp = getIndianTimestamp();

        try {
            const profileSnap = await get(ref(getDatabase(), `profileDashboard/${userId}`));
            const companyName = profileSnap.val()?.company_name || "Customer";

            await set(ref(getDatabase(), `transport_goods/${userId}/${transportId}`), {
                ...formData,
                companyName,
                timestamp
            });

            await sendNotification(transportId, companyName);

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
            setFormData({ destinationFrom: '', destinationTo: '', commodity: '', quantity: '', companyName: '' });
        } catch (err) {
            alert("Failed to submit. Try again.");
        }
    };

    // Loading state
    if (loading) {
        return <div className="loading-container"><div className="loader"></div></div>;
    }

    // Only show error if truly no account (almost never)
    if (!userId) {
        return null; // or redirect
    }

    return (
        <div className='container-fluid transport-fluid'>
            <div className='transport-goods-container'>

                {/* Left: Transport Form */}
                <div className='transport-form-container'>
                    <h3 className='text-center fw-bold trans-text'>
                        Transport Goods <FaInfoCircle style={{ fontSize: '18px', color: '#ea8716', cursor: 'pointer' }}
                            onClick={() => alert("Submit transport details and receive supplier responses.")} />
                    </h3>

                    <form onSubmit={handleSubmit}>
                        <div className='transport-form'>
                            <label className='transport-label'>Destination From</label>
                            <input type="text" name="destinationFrom" value={formData.destinationFrom} onChange={handleChange} required className='transport-input' />
                        </div>
                        <div className='transport-form'>
                            <label className='transport-label'>Destination To</label>
                            <input type="text" name="destinationTo" value={formData.destinationTo} onChange={handleChange} required className='transport-input' />
                        </div>
                        <div className='transport-form'>
                            <label className='transport-label'>Commodity</label>
                            <select name="commodity" value={formData.commodity} onChange={handleChange} required className='transport-input'>
                                <option value="">Select a commodity</option>
                                {commodityOptions.map((commodity, index) => (
                                    <option key={index} value={commodity}>{commodity}</option>
                                ))}
                            </select>
                        </div>
                        <div className='transport-form'>
                            <label className='transport-label'>Quantity (bags)</label>
                            <input
                                type="number"
                                name="quantity"
                                value={formData.quantity}
                                onChange={handleChange}
                                min={1}
                                required
                                className='transport-input'
                                onWheel={(e) => e.target.blur()}
                            />
                            {error && <p className="error-message" style={{ color: 'red', fontSize: '14px' }}>{error}</p>}
                        </div>

                        <button type="submit" className='transport-submit'>Submit</button>
                        {success && <div className="success-message">Form submitted successfully!</div>}
                    </form>
                </div>

                {/* Right: Supplier Responses — YOUR EXACT UI */}
                <div className="supplier-replies">
                    <h4 className='text-center fw-bold'>Supplier Replies</h4>
                    {Object.keys(requests).length === 0 ? (
                        <p className='text-danger fw-bold text-center'>No requests found.</p>
                    ) : (
                        Object.entries(requests).map(([transportId, requestData]) => (
                            <div key={transportId} className="transport-request-card">
                                <p><strong>From:</strong> {requestData.destinationFrom || 'N/A'}</p>
                                <p><strong>To:</strong> {requestData.destinationTo || 'N/A'}</p>
                                <p><strong>Commodity:</strong> {requestData.commodity || 'N/A'}</p>
                                <p><strong>Quantity:</strong> {requestData.quantity || 'N/A'} (bags)</p>

                                {/* SUPPLIER REPLIES — NOW WORKS IN REAL-TIME */}
                                {replies[transportId] ? (
                                    Object.entries(replies[transportId]).map(([supplierId, reply]) => (
                                        <div key={supplierId} className={`supplier-reply ${getSupplierClass(supplierId)}`}>
                                            <p><strong>Supplier:</strong> {supplierId}</p>
                                            <p><strong>Price:</strong> {reply.price}</p>
                                            <p><strong>Availability:</strong> {reply.availability}</p>
                                            <p><strong>Estimated Delivery:</strong> {reply.estimatedDelivery}</p>
                                            <p><strong>Transporter:</strong> {reply.transporter?.name} (Contact: {reply.transporter?.contact})</p>
                                            {reply.notes && <p><strong>Notes:</strong> {reply.notes}</p>}
                                        </div>
                                    ))
                                ) : (
                                    <p className='text-danger fw-bold'>No supplier replies yet.</p>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

export default TransportGoods;